import React, { useState } from 'react';
import {
  Table, Button, Select, TimePicker, InputNumber, Space, Popconfirm,
  message, Alert, Card, Form, Input, DatePicker, Row, Col,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { createSchedule, deleteSchedule, updateAgreement } from '../../api/preschools';
import useAuthStore from '../../store/authStore';
import { apiErrorMessage } from '../../utils/apiError';

/** Carbon dayOfWeek ile AYNI numaralandırma: 0=Pazar ... 6=Cumartesi. */
const GUNLER = [
  { value: 1, label: 'Pazartesi' },
  { value: 2, label: 'Salı' },
  { value: 3, label: 'Çarşamba' },
  { value: 4, label: 'Perşembe' },
  { value: 5, label: 'Cuma' },
  { value: 6, label: 'Cumartesi' },
  { value: 0, label: 'Pazar' },
];

/**
 * Haftalık program: hangi gün, saat kaçta, kaç saat. Okuldan okula
 * değişiyor — ders günleri bu programdan üretiliyor.
 *
 * Anlaşma ayarları (tutar, ödeme günü, bitiş) da burada çünkü ikisi aynı
 * soruyu cevaplıyor: "bu okulla şartlar ne".
 */
const PreschoolScheduleTab = ({ agreement, onChange }) => {
  const { user } = useAuthStore();
  const finansVar = (user?.permissions || []).includes('preschools.finance');

  const [saving, setSaving] = useState(false);
  const [gun, setGun] = useState(1);
  const [saat, setSaat] = useState(null);
  const [sure, setSure] = useState(1);
  const [form] = Form.useForm();
  // Secili mod degisince 'kac ders' alani gorunur/gizlenir.
  const odemeModu = Form.useWatch('payment_basis', form) || agreement.payment_basis || 'monthly';

  const ekle = async () => {
    setSaving(true);
    try {
      await createSchedule(agreement.id, {
        weekday: gun,
        // API'ye giden saat H:i - ham dayjs nesnesi gonderilirse ISO'ya
        // cevrilir ve dogrulama reddeder.
        start_time: saat ? saat.format('HH:mm') : null,
        hours: sure,
      });
      message.success('Program satırı eklendi.');
      setSaat(null);
      onChange();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Eklenemedi.'));
    } finally {
      setSaving(false);
    }
  };

  const anlasmaKaydet = async (values) => {
    setSaving(true);
    try {
      await updateAgreement(agreement.id, {
        preschool_season_id: agreement.preschool_season_id,
        monthly_fee: values.monthly_fee,
        payment_day: values.payment_day,
        payment_basis: values.payment_basis,
        lessons_per_payment: values.lessons_per_payment,
        certificate_term_count: values.certificate_term_count,
        // Tarih alanlari .format('YYYY-MM-DD') ile gonderilir - ham dayjs
        // nesnesi UTC'ye cevrilirken bir gun geriye kayar (CLAUDE.md).
        start_date: values.start_date ? values.start_date.format('YYYY-MM-DD') : null,
        end_date: values.end_date ? values.end_date.format('YYYY-MM-DD') : null,
        end_reason: values.end_reason || null,
      });
      message.success('Anlaşma güncellendi.');
      onChange();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Güncellenemedi.'));
    } finally {
      setSaving(false);
    }
  };

  const satirlar = agreement.schedules || [];

  return (
    <div>
      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="Ders günleri bu programdan üretiliyor"
        description="Haftalık programı tanımladıktan sonra Ders Günleri sekmesinden takvimi oluşturabilirsiniz. Üretim var olan günleri atlar, asla silmez."
      />

      <Table
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={satirlar}
        locale={{ emptyText: 'Haftalık program tanımlanmamış.' }}
        columns={[
          {
            title: 'Gün',
            dataIndex: 'weekday',
            render: (v) => GUNLER.find((g) => g.value === v)?.label ?? v,
          },
          {
            title: 'Saat',
            dataIndex: 'start_time',
            render: (v) => (v ? String(v).slice(0, 5) : '—'),
          },
          { title: 'Süre', dataIndex: 'hours', render: (v) => `${Number(v)} saat` },
          {
            title: '',
            key: 'sil',
            width: 50,
            render: (_, r) => (
              <Popconfirm
                title="Program satırı silinsin mi?"
                description="Üretilmiş ders günleri silinmez."
                onConfirm={async () => {
                  try {
                    await deleteSchedule(agreement.id, r.id);
                    message.success('Silindi.');
                    onChange();
                  } catch (error) {
                    message.error(apiErrorMessage(error, 'Silinemedi.'));
                  }
                }}
                okText="Sil"
                cancelText="Vazgeç"
              >
                <Button danger size="small" icon={<DeleteOutlined />} />
              </Popconfirm>
            ),
          },
        ]}
      />

      <Space className="mt-3" wrap>
        <Select value={gun} onChange={setGun} options={GUNLER} style={{ width: 140 }} />
        <TimePicker value={saat} onChange={setSaat} format="HH:mm" minuteStep={5} placeholder="Saat" />
        <InputNumber value={sure} onChange={setSure} min={0.5} max={12} step={0.5} addonAfter="saat" />
        <Button type="primary" icon={<PlusOutlined />} onClick={ekle} loading={saving}>
          Ekle
        </Button>
      </Space>

      {finansVar && (
        <Card size="small" title="Anlaşma Şartları" className="mt-6">
          <Form
            form={form}
            layout="vertical"
            onFinish={anlasmaKaydet}
            initialValues={{
              monthly_fee: agreement.monthly_fee,
              payment_day: agreement.payment_day,
              payment_basis: agreement.payment_basis || 'monthly',
              lessons_per_payment: agreement.lessons_per_payment || 4,
              certificate_term_count: agreement.certificate_term_count,
              start_date: agreement.start_date ? dayjs(agreement.start_date) : null,
              end_date: agreement.end_date ? dayjs(agreement.end_date) : null,
              end_reason: agreement.end_reason,
            }}
          >
            <Row gutter={12}>
              <Col xs={24} md={8}>
                <Form.Item name="monthly_fee" label="Aylık Tutar (₺)" rules={[{ required: true }]}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={12} md={8}>
                <Form.Item name="payment_day" label="Ödeme Günü" rules={[{ required: true }]}>
                  <InputNumber min={1} max={31} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={12} md={8}>
                <Form.Item name="certificate_term_count" label="Sezonda Sertifika" rules={[{ required: true }]}>
                  <Select options={[1, 2, 3, 4].map((n) => ({ value: n, label: `${n} adet` }))} />
                </Form.Item>
              </Col>
            </Row>

            {/*
              ÖDEME MODU — kurumdan kuruma değişiyor ve SEZON ORTASINDA
              değişebiliyor ("aylık başladı, haftalığa döndü, sonra tekrar
              aylığa").

              Burada seçilen mod "BUNDAN SONRASI" demek: üretilmiş ödeme
              satırları hangi modla doğduklarını kendi içlerinde saklıyor,
              yani modu değiştirmek geçmiş satırları BOZMUYOR. Üretim de
              son faturalanmış dönemden sonrasını hesapladığı için aynı
              dönem iki kez faturalanmıyor.
            */}
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="payment_basis"
                  label="Ödeme Şekli"
                  extra={odemeModu === 'lesson_count'
                    ? 'Ders YAPILDIKÇA borç oluşur; yapılmayan ders dönemi doldurmaz.'
                    : 'Her takvim ayı için bir borç oluşur; dersin yapılıp yapılmaması tutarı değiştirmez.'}
                >
                  <Select
                    options={[
                      { value: 'monthly', label: 'Aylık (takvim ayı)' },
                      { value: 'lesson_count', label: 'Ders günü bazlı' },
                    ]}
                  />
                </Form.Item>
              </Col>
              {odemeModu === 'lesson_count' && (
                <Col xs={24} md={12}>
                  <Form.Item
                    name="lessons_per_payment"
                    label="Kaç ders günü = bir ödeme"
                    rules={[{ required: true }]}
                    extra="DERS GÜNÜ sayılır, hafta değil: haftada iki gün gidilen okulda 4 ders iki haftada dolar."
                  >
                    <InputNumber min={1} max={60} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              )}
            </Row>

            <div className="text-sm text-gray-500 mb-2">
              Anlaşma sezon ortasında bitirildiyse bitiş tarihini yazın — ödeme
              planı yalnızca o aya kadar üretilir.
            </div>
            <Row gutter={12}>
              <Col xs={12} md={8}>
                <Form.Item name="start_date" label="Başlangıç (boşsa sezon başı)">
                  <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={12} md={8}>
                <Form.Item name="end_date" label="Bitiş (boşsa sezon sonu)">
                  <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="end_reason" label="Bitiş Sebebi">
                  <Input placeholder="Okul 2. dönem devam etmedi" />
                </Form.Item>
              </Col>
            </Row>

            <Button type="primary" htmlType="submit" loading={saving}>Kaydet</Button>
          </Form>
        </Card>
      )}
    </div>
  );
};

export default PreschoolScheduleTab;
